import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Plus, Loader2, Trash2, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";

const money = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Finance() {
  const [tx, setTx] = useState([]);
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ type: "receita", description: "", amount: "", category: "", status: "pendente", date: new Date().toISOString().slice(0, 10) });

  const load = () => {
    api.get("/finance/transactions").then((r) => setTx(r.data));
    api.get("/finance/summary").then((r) => setSum(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.description.trim() || !f.amount) { toast.error("Preencha descrição e valor"); return; }
    setSaving(true);
    try {
      await api.post("/finance/transactions", { ...f, amount: parseFloat(f.amount) });
      toast.success("Lançamento registrado");
      setOpen(false); setF({ ...f, description: "", amount: "", category: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePaid = async (t) => {
    await api.put(`/finance/transactions/${t.id}`, { status: t.status === "pago" ? "pendente" : "pago" });
    load();
  };
  const remove = async (id) => { await api.delete(`/finance/transactions/${id}`); load(); };

  const cards = [
    { label: "Receitas (pagas)", value: sum?.receitas, icon: TrendingUp, color: "text-success" },
    { label: "Despesas (pagas)", value: sum?.despesas, icon: TrendingDown, color: "text-destructive" },
    { label: "Saldo", value: sum?.saldo, icon: Wallet, color: "text-primary" },
    { label: "Inadimplência", value: sum?.inadimplencia, icon: AlertCircle, color: "text-warning" },
  ];

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2">Financeiro</p>
          <h1 className="font-heading text-4xl font-light tracking-tighter">Caixa & cobranças</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-transaction-btn"><Plus className="w-4 h-4 mr-2" /> Lançamento</Button>
          </DialogTrigger>
          <DialogContent data-testid="transaction-dialog">
            <DialogHeader><DialogTitle>Novo lançamento</DialogTitle>
              <DialogDescription>Registre uma receita ou despesa.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Tipo</Label>
                  <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                    <SelectTrigger data-testid="tx-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receita">Receita</SelectItem>
                      <SelectItem value="despesa">Despesa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                    <SelectTrigger data-testid="tx-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="pendente">Pendente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Descrição</Label>
                <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="tx-desc-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Valor (R$)</Label>
                  <Input type="number" step="any" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="tx-amount-input" /></div>
                <div className="space-y-2"><Label>Categoria</Label>
                  <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="tx-category-input" placeholder="ex: consulta" /></div>
              </div>
              <div className="space-y-2"><Label>Data</Label>
                <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="tx-date-input" /></div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-transaction-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="p-5 border-border shadow-none" data-testid={`finance-${c.label}`}>
              <Icon className={`w-5 h-5 ${c.color} mb-2`} />
              <div className="font-mono-data text-xl font-semibold">{money(c.value)}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
            </Card>
          );
        })}
      </div>

      {tx.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">Nenhum lançamento.</Card>
      ) : (
        <div className="space-y-2">
          {tx.map((t) => (
            <Card key={t.id} className="p-4 border-border shadow-none flex items-center justify-between" data-testid={`tx-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.type === "receita" ? "bg-success/12 text-success" : "bg-destructive/12 text-destructive"}`}>
                  {t.type === "receita" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                </div>
                <div>
                  <p className="text-sm font-medium">{t.description}</p>
                  <p className="text-xs text-muted-foreground">{t.date}{t.category ? ` · ${t.category}` : ""}{t.patient_name ? ` · ${t.patient_name}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-mono-data font-semibold ${t.type === "receita" ? "text-success" : "text-destructive"}`}>
                  {t.type === "receita" ? "+" : "−"}{money(t.amount)}
                </span>
                <Badge variant="outline" onClick={() => togglePaid(t)} data-testid={`tx-status-${t.id}`}
                  className={`cursor-pointer ${t.status === "pago" ? "bg-success/12 text-success border-success/25" : "bg-warning/15 text-warning border-warning/30"}`}>
                  {t.status}
                </Badge>
                <Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`delete-tx-${t.id}`}>
                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
